import { createAsyncThunk } from "@reduxjs/toolkit";

import { StatusCodes, ReducerEntityPrefixTypes, ReducerNamespaceTypes, LicenseTypes } from "enums";
import { makeActionCreator } from "features/Application/actions/actionsBuilder";
import dataService from "features/Application/services/dataServices/typedDataService";
import { getPrefix } from "features/Application/slices/models";
import { type GetPacksRequest, type PacksRequest } from "features/Licensing/Packs/types/requests";
import * as editAccountActionTypes from "features/Accounts/EditAccount/state/editAccountActionTypes";
import { type FetchActionPayload } from "interfaces/redux";
import { type AssignAccountPack } from "features/Accounts/PackAssignmentModal/types";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { type UpdateParams, type DistributedOpUpdateParams } from "interfaces/updateParams";
import { pluralize } from "utils/stringUtils";
import backgroundTask from "features/BackgroundTasks/backgroundTask";
import { type AccountPack } from "features/Accounts/types";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;
const loadGridItemsCount: any = process.env.REACT_APP_LOAD_ITEMS_COUNT;

const fetchAccountPackForbidden = makeActionCreator(editAccountActionTypes.FETCH_ACCOUNT_PACKS_FORBIDDEN, "error");

export const fetchAccountPacksGrid = createAsyncThunk<FetchActionPayload<AccountPack>, PacksRequest>(
  getPrefix({
    namespace: ReducerNamespaceTypes.Licensing,
    entity: ReducerEntityPrefixTypes.AccountPacks,
    name: "gridItems",
  }),
  async (requestData: PacksRequest, { signal, dispatch }) => {
    const request: GetPacksRequest = {
      top: loadGridItemsCount,
      skip: requestData.skip,
      orderBy: requestData.orderBy,
      searchTerm: requestData.searchTerm,
    };

    try {
      const { getDataPromise, cancel } = dataService.packs.getAccountPacksWithCancel(
        requestData.accountId,
        request,
        requestData.filters || {},
      );

      signal.addEventListener("abort", cancel);

      const result = await getDataPromise;
      const recordsCount = Number.parseInt(result.headers[countHeaderName]);

      return {
        items: result.data,
        totalCount: recordsCount,
      };
    } catch (error: any) {
      if (error.response?.status === StatusCodes.Forbidden) {
        dispatch(fetchAccountPackForbidden(error));
      }

      throw error;
    }
  },
);

export const addPacksToAccount = (packs: AssignAccountPack[], accountId: number, onCompleted?: () => void) => {
  return async (dispatch: AppDispatch) => {
    const updateParams: DistributedOpUpdateParams = {
      id: "AddPacksToAccount",
      title: "Add packs to account",
      getOperationProps: async () => {
        const { data } = await dataService.packs.addPacksToAccountsAsync(accountId, {
          packInfos: packs.map((pack) => ({
            ...pack,
            licenseType: pack.noExpiration && !pack.licenseType ? LicenseTypes.CustomDate : pack.licenseType,
          })),
        });
        return {
          operationId: data.operationId,
          stepsIds: data.stepsIds,
          statusUrl: data.statusUrl,
        };
      },
      successTransientMessage: `${pluralize("Pack", packs.length)} has been added to account successfully`,
      failureTransientMessage: `Adding ${pluralize("pack", packs.length)} to account failed!`,
      onCompleted,
    };
    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };
};

export const removePackFromAccount = (accountId: number, packId: number, onCompleted?: () => void) => {
  return async (dispatch: AppDispatch) => {
    const updateParams: UpdateParams = {
      id: "RemovePack",
      title: "Removing Pack from Account",
      getMessageIds: async () => {
        const messageId = await dataService.accounts.removePackFromAccount(accountId, packId);
        return [messageId];
      },
      successTransientMessage: "Pack removed from account",
      failureTransientMessage: "Failed to remove pack from account",
      indeterminate: true,
      onCompleted,
    };

    await backgroundTask.initializeBackgroundTask(updateParams, dispatch);
  };
};

export const archivePack = (accountId: number, packId: number) => {
  return async (dispatch: AppDispatch) => {
    const updateParams: DistributedOpUpdateParams = {
      id: "ArchivePack",
      title: "Archive Pack",
      indeterminate: true,
      getOperationProps: async () => {
        const { data } = await dataService.packs.archivePack(accountId, packId);
        return data;
      },
      successTransientMessage: "Pack was archived.",
      failureTransientMessage: "Pack archive failed!",
    };

    await backgroundTask.initializeDistributedBackgroundTask(updateParams, dispatch);
  };
};

export type AccountPacksGridFetchType = ReturnType<ReturnType<typeof fetchAccountPacksGrid>>;
